import type {
  SchoolSettingsContract,
  StudentLifecycleImpactContract,
  StudentLifecycleMutationResultContract,
  StudentLifecycleStatusContract,
  StudentProfileContract,
  StudentSectionLabelContract,
} from "@chams-pay/contracts";
import { sectionRequiredForLevel } from "@chams-pay/contracts";
import { useCallback, useEffect, useMemo, useState } from "react";
import { casablancaDate } from "./format.js";
import { deviceFetch } from "./client.js";

export type StudentLifecycleMode = "withdrawal" | "graduation" | "return" | "correction";

async function responseJson<T>(response: Response): Promise<T> {
  const body = (await response.json()) as T & { code?: string };
  if (!response.ok) throw new Error(body.code ?? `HTTP_${response.status}`);
  return body;
}

function serviceEnabled(student: StudentProfileContract, service: "transport" | "accompaniment") {
  return new RegExp(service, "i").test(student.serviceSummaryFr);
}

function nextDate(value: string): string {
  const date = new Date(`${value}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + 1);
  return date.toISOString().slice(0, 10);
}

export interface StudentLifecycleController {
  readonly mode: StudentLifecycleMode | null;
  readonly effectiveDate: string;
  readonly reason: string;
  readonly resultingStatus: StudentLifecycleStatusContract;
  readonly classLabel: string;
  readonly sectionLabel: StudentSectionLabelContract | null;
  readonly transport: boolean;
  readonly accompaniment: boolean;
  readonly confirmed: boolean;
  readonly impact: StudentLifecycleImpactContract | null;
  readonly previewing: boolean;
  readonly busy: boolean;
  readonly failedCode: string | null;
  readonly classOptions: readonly string[];
  readonly sectionOptions: readonly StudentSectionLabelContract[];
  readonly minimumDate: string;
  readonly maximumDate: string;
  readonly submitDisabled: boolean;
  readonly setEffectiveDate: (value: string) => void;
  readonly setReason: (value: string) => void;
  readonly setResultingStatus: (value: StudentLifecycleStatusContract) => void;
  readonly setClassLabel: (value: string) => void;
  readonly setSectionLabel: (value: StudentSectionLabelContract | null) => void;
  readonly setTransport: (value: boolean) => void;
  readonly setAccompaniment: (value: boolean) => void;
  readonly setConfirmed: (value: boolean) => void;
  readonly start: (mode: StudentLifecycleMode) => void;
  readonly close: () => void;
  readonly submit: () => Promise<void>;
}

export function useStudentLifecycle({
  student,
  csrf,
  onSaved,
}: {
  readonly student: StudentProfileContract;
  readonly csrf: string;
  readonly onSaved: (profile: StudentProfileContract) => void;
}): StudentLifecycleController {
  const [mode, setMode] = useState<StudentLifecycleMode | null>(null);
  const [effectiveDate, setEffectiveDate] = useState(casablancaDate());
  const [reason, setReason] = useState("");
  const [resultingStatus, setResultingStatus] = useState<StudentLifecycleStatusContract>(
    student.lifecycleStatus,
  );
  const [classLabel, setClassLabel] = useState(student.currentClass);
  const [sectionLabel, setSectionLabel] = useState<StudentSectionLabelContract | null>(
    student.sectionLabel,
  );
  const [transport, setTransport] = useState(serviceEnabled(student, "transport"));
  const [accompaniment, setAccompaniment] = useState(serviceEnabled(student, "accompaniment"));
  const [confirmed, setConfirmed] = useState(false);
  const [impact, setImpact] = useState<StudentLifecycleImpactContract | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failedCode, setFailedCode] = useState<string | null>(null);
  const [settings, setSettings] = useState<SchoolSettingsContract | null>(null);
  const today = casablancaDate();

  const start = useCallback(
    (nextMode: StudentLifecycleMode) => {
      const latest = student.lifecycle.events[0];
      setMode(nextMode);
      setEffectiveDate(
        nextMode === "correction" && latest ? latest.effectiveDate : casablancaDate(),
      );
      setReason("");
      setResultingStatus(student.lifecycleStatus);
      setClassLabel(student.currentClass);
      setSectionLabel(student.sectionLabel);
      setTransport(serviceEnabled(student, "transport"));
      setAccompaniment(serviceEnabled(student, "accompaniment"));
      setConfirmed(false);
      setImpact(null);
      setFailedCode(null);
      if ((nextMode === "return" || nextMode === "correction") && !settings) {
        void deviceFetch("/api/admin/school-settings")
          .then((response) => responseJson<SchoolSettingsContract>(response))
          .then(setSettings)
          .catch((error: unknown) =>
            setFailedCode(error instanceof Error ? error.message : "STUDENT_LIFECYCLE_FAILED"),
          );
      }
    },
    [settings, student],
  );

  const close = useCallback(() => {
    if (busy) return;
    setMode(null);
    setConfirmed(false);
    setFailedCode(null);
  }, [busy]);

  useEffect(() => {
    if (mode !== "withdrawal" && mode !== "graduation") return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(effectiveDate)) {
      setImpact(null);
      return;
    }
    const controller = new AbortController();
    setPreviewing(true);
    setFailedCode(null);
    const url = `/api/students/${student.id}/lifecycle-impact?kind=${mode}&effectiveDate=${encodeURIComponent(effectiveDate)}`;
    void deviceFetch(url, { signal: controller.signal })
      .then((response) => responseJson<StudentLifecycleImpactContract>(response))
      .then((value) => {
        if (!controller.signal.aborted) setImpact(value);
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          setImpact(null);
          setFailedCode(error instanceof Error ? error.message : "STUDENT_LIFECYCLE_FAILED");
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setPreviewing(false);
      });
    return () => controller.abort();
  }, [effectiveDate, mode, student.id]);

  useEffect(() => {
    setConfirmed(false);
  }, [accompaniment, classLabel, effectiveDate, reason, resultingStatus, sectionLabel, transport]);

  useEffect(() => {
    if (settings && !sectionRequiredForLevel(settings.classes, classLabel)) setSectionLabel(null);
  }, [classLabel, settings]);

  const minimumDate = useMemo(() => {
    if (mode === "return")
      return [nextDate(student.lifecycle.effectiveDate), settings?.activeFrom ?? ""]
        .filter(Boolean)
        .sort()
        .at(-1)!;
    if (
      mode === "correction" &&
      resultingStatus === "active" &&
      settings &&
      student.enrollment.academicYear !== settings.academicYear
    )
      return settings.activeFrom;
    return student.enrollment.enrolledAt.slice(0, 10);
  }, [
    mode,
    resultingStatus,
    settings,
    student.enrollment.academicYear,
    student.enrollment.enrolledAt,
    student.lifecycle.effectiveDate,
  ]);

  const maximumDate = useMemo(() => {
    const usesCurrentSettings =
      mode === "return" ||
      (mode === "correction" &&
        resultingStatus === "active" &&
        settings &&
        student.enrollment.academicYear !== settings.academicYear);
    return usesCurrentSettings && settings?.activeThrough && settings.activeThrough < today
      ? settings.activeThrough
      : today;
  }, [mode, resultingStatus, settings, student.enrollment.academicYear, today]);

  const pendingFuture = BigInt(impact?.pendingFutureMad ?? "0") > 0n;
  const sectionOptions: readonly StudentSectionLabelContract[] =
    settings && sectionRequiredForLevel(settings.classes, classLabel) ? ["A", "B"] : [];
  const submitDisabled =
    busy ||
    previewing ||
    !confirmed ||
    reason.trim().length < 3 ||
    !/^\d{4}-\d{2}-\d{2}$/.test(effectiveDate) ||
    effectiveDate < minimumDate ||
    effectiveDate > maximumDate ||
    ((mode === "withdrawal" || mode === "graduation") && (!impact || pendingFuture)) ||
    (mode === "correction" && !settings) ||
    (mode === "return" &&
      (!settings ||
        !classOptionsOf(settings).includes(classLabel) ||
        (sectionOptions.length > 0
          ? !sectionOptions.includes(sectionLabel as StudentSectionLabelContract)
          : sectionLabel !== null)));

  const submit = useCallback(async () => {
    if (!mode || submitDisabled) return;
    setBusy(true);
    setFailedCode(null);
    try {
      const body = {
        effectiveDate,
        reason: reason.trim(),
        ...(mode === "return" ? { classLabel, sectionLabel, transport, accompaniment } : {}),
        ...(mode === "correction" ? { resultingStatus } : {}),
      };
      const result = await responseJson<StudentLifecycleMutationResultContract>(
        await deviceFetch(`/api/students/${student.id}/lifecycle/${mode}`, {
          method: "POST",
          headers: { "content-type": "application/json", "x-csrf-token": csrf },
          body: JSON.stringify(body),
        }),
      );
      onSaved(result.student);
      setMode(null);
      setConfirmed(false);
    } catch (error) {
      setFailedCode(error instanceof Error ? error.message : "STUDENT_LIFECYCLE_FAILED");
    } finally {
      setBusy(false);
    }
  }, [
    accompaniment,
    classLabel,
    csrf,
    effectiveDate,
    mode,
    onSaved,
    reason,
    resultingStatus,
    sectionLabel,
    student.id,
    submitDisabled,
    transport,
  ]);

  return {
    mode,
    effectiveDate,
    reason,
    resultingStatus,
    classLabel,
    sectionLabel,
    transport,
    accompaniment,
    confirmed,
    impact,
    previewing,
    busy,
    failedCode,
    classOptions: settings ? classOptionsOf(settings) : [],
    sectionOptions,
    minimumDate,
    maximumDate,
    submitDisabled,
    setEffectiveDate,
    setReason,
    setResultingStatus,
    setClassLabel: (value) => {
      setClassLabel(value);
      if (!settings || !sectionRequiredForLevel(settings.classes, value)) setSectionLabel(null);
    },
    setSectionLabel,
    setTransport,
    setAccompaniment,
    setConfirmed,
    start,
    close,
    submit,
  };
}

function classOptionsOf(settings: SchoolSettingsContract): readonly string[] {
  return settings.classes.map((entry) => entry.label);
}
